import { useEffect, useState } from 'react'
import { TopBar } from '@/widgets/TopBar'
import { TabBar } from '@/widgets/TabBar'
import { ChatWidget } from '@/widgets/ChatWidget'
import { Sidebar } from '@/widgets/Sidebar'
import { useTabsStore } from '@/shared/stores/tabs.store'
import { useConnectionStore } from '@/shared/stores/connection.store'
import { useBuildVersionStore } from '@/shared/lib/build-version-guard'
import { ErrorBoundary } from '@/shared/ui/ErrorBoundary'
import { useCreateSession } from '@/features/create-session'

export function ChatPage() {
  const { createSession } = useCreateSession()
  const activeTabId = useTabsStore(s => s.activeTabId)
  const tabs = useTabsStore(s => s.tabs)
  const activeTab = tabs.find(t => t.id === activeTabId)
  const connectionStatus = useConnectionStore(s => s.status)
  const updateAvailable = useBuildVersionStore(s => s.updateAvailable)
  const updateBannerDismissed = useBuildVersionStore(s => s.dismissed)
  const dismissUpdateBanner = useBuildVersionStore(s => s.dismiss)

  // Sidebar toggle desktop — état persisté en localStorage
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem('mb-sidebar-open')
      return stored === null ? true : stored === 'true'
    } catch {
      return true
    }
  })

  const handleSidebarToggle = () => {
    setSidebarOpen(prev => {
      const next = !prev
      try {
        localStorage.setItem('mb-sidebar-open', String(next))
      } catch {
        // localStorage indisponible — état en mémoire uniquement
      }
      return next
    })
  }

  // Sidebar mobile overlay — pas de persist (toujours fermé par défaut)
  const [sidebarMobileOpen, setSidebarMobileOpen] = useState(false)

  const handleMobileOpen = () => setSidebarMobileOpen(true)
  const handleMobileClose = () => setSidebarMobileOpen(false)

  // Listener resize : si fenêtre passe en desktop pendant que l'overlay est ouvert → fermer
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 768px)')
    const handleChange = (e: MediaQueryListEvent) => {
      if (e.matches && sidebarMobileOpen) {
        setSidebarMobileOpen(false)
      }
    }
    mql.addEventListener('change', handleChange)
    return () => { mql.removeEventListener('change', handleChange) }
  }, [sidebarMobileOpen])

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        // 100% (pas 100dvh) : dvh est une unite viewport absolue, non contrainte par
        // le parent — elle empecherait le DebugPanel docke (App.tsx) de pousser cette
        // page vers le haut. Le parent (App.tsx) fixe deja la hauteur reelle disponible.
        height: '100%',
        background: 'var(--color-bg-base)',
      }}
    >
      <ErrorBoundary name="TopBar">
        <TopBar onToggleSidebar={handleMobileOpen} onCreateSession={createSession} />
      </ErrorBoundary>

      {/* Reconnection banner */}
      {connectionStatus === 'reconnecting' && (
        <div style={{
          background: 'var(--color-warning, #f59e0b)',
          color: '#000',
          padding: 'var(--space-2) var(--space-4)',
          textAlign: 'center',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-xs)',
          fontWeight: 500,
        }}>
          Reconnexion en cours... Le texte en cours de saisie est sauvegarde.
        </div>
      )}

      {connectionStatus === 'offline' && (
        <div style={{
          background: 'var(--color-error, #ef4444)',
          color: '#fff',
          padding: 'var(--space-2) var(--space-4)',
          textAlign: 'center',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-xs)',
          fontWeight: 500,
        }}>
          Connexion perdue. Verifiez votre connexion internet.
        </div>
      )}

      {/* Nouvelle version du build client disponible (backlog #10597) — jamais de rechargement
          automatique : Fabrice declenche lui-meme, quand il le souhaite. Refermable (bouton X),
          mais reapparait au prochain retour sur l'onglet tant qu'il n'a pas recharge : fermer
          veut dire "pas maintenant", jamais "ne me le redis plus" (App.tsx gere la reapparition). */}
      {updateAvailable && !updateBannerDismissed && (
        <div style={{
          background: 'var(--color-accent)',
          color: 'var(--color-on-accent)',
          padding: 'var(--space-2) var(--space-4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--space-3)',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-xs)',
          fontWeight: 500,
        }}>
          <span>Nouvelle version disponible.</span>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: 'var(--color-on-accent)',
              color: 'var(--color-accent)',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              padding: '2px 10px',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-xs)',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Recharger
          </button>
          <button
            onClick={dismissUpdateBanner}
            aria-label="Fermer"
            style={{
              background: 'transparent',
              color: 'var(--color-on-accent)',
              border: 'none',
              fontSize: 'var(--text-sm)',
              lineHeight: 1,
              cursor: 'pointer',
              padding: '0 var(--space-1)',
            }}
          >
            ×
          </button>
        </div>
      )}

      <ErrorBoundary name="TabBar">
        <TabBar />
      </ErrorBoundary>
      <div
        style={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        {/* Slot panneau gauche (Explorer) — reserve pour V3.1 */}
        <aside
          className="explorer-slot"
          style={{
            width: 'var(--explorer-width)',
            overflow: 'hidden',
            flexShrink: 0,
            transition: 'width var(--duration-base)',
          }}
          aria-hidden="true"
          data-slot="explorer-panel"
        />
        <main
          style={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <ErrorBoundary name="ChatWidget">
            {activeTab?.type === 'conversation' && activeTab.sessionId ? (
              <ChatWidget key={activeTab.sessionId} sessionId={activeTab.sessionId} />
            ) : (
              <EmptyState />
            )}
          </ErrorBoundary>
        </main>
        {/* Sidebar droite — bouton toggle + widget (desktop uniquement, masqué en mobile via Sidebar.css) */}
        {/* Le bouton toggle est HORS du conteneur overflow:hidden pour rester visible quand sidebar fermée */}
        <div
          style={{
            display: 'flex',
            flexShrink: 0,
            alignItems: 'stretch',
          }}
          data-slot="sidebar-area"
        >
          {/* Bouton toggle chevron — toujours visible, colle au bord gauche de la sidebar */}
          <button
            onClick={handleSidebarToggle}
            aria-label={sidebarOpen ? 'Masquer la sidebar' : 'Afficher la sidebar'}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              alignSelf: 'center',
              width: 20,
              height: 40,
              background: 'var(--color-bg-surface)',
              border: '1px solid var(--color-border)',
              borderRight: 'none',
              borderRadius: 'var(--radius-sm) 0 0 var(--radius-sm)',
              cursor: 'pointer',
              color: 'var(--color-text-muted)',
              fontSize: 'var(--text-sm)',
              lineHeight: 1,
              padding: 0,
              flexShrink: 0,
              zIndex: 10,
            }}
          >
            {sidebarOpen ? '›' : '‹'}
          </button>
          {/* Conteneur sidebar avec transition width */}
          <div
            style={{
              width: sidebarOpen ? 'var(--sidebar-width)' : 0,
              overflow: 'hidden',
              flexShrink: 0,
              transition: 'width 200ms ease',
            }}
            data-slot="sidebar-container"
          >
            <ErrorBoundary name="Sidebar">
              <Sidebar isOpen={sidebarOpen} onToggle={handleSidebarToggle} />
            </ErrorBoundary>
          </div>
        </div>
      </div>

      {/* Overlay Sidebar mobile — rendu hors flux, position fixed */}
      {sidebarMobileOpen && (
        <ErrorBoundary name="Sidebar (mobile)">
          <Sidebar
            isOpen={true}
            onToggle={handleMobileClose}
            isOverlay={true}
            onMobileClose={handleMobileClose}
          />
        </ErrorBoundary>
      )}
    </div>
  )
}

function EmptyState() {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--color-text-muted)',
      }}
    >
      <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)' }}>
        Aucune conversation ouverte.
      </p>
    </div>
  )
}
